(() => {
  const gallerySelector = '[data-cep-swipe-gallery]';
  const mobileViewport = window.matchMedia('(max-width: 749px)');

  const initializeLightbox = (gallery) => {
    const trigger = gallery.querySelector('[data-cep-zoom]');
    const dialog = gallery.querySelector('[data-cep-lightbox]');
    if (!trigger || !dialog || typeof dialog.showModal !== 'function' || gallery.dataset.cepLightboxReady === 'true') return;
    const photos = Array.from(gallery.querySelectorAll('.cep-slide img'));
    const radios = Array.from(gallery.querySelectorAll('.cep-radio'));
    if (!photos.length) return;
    gallery.dataset.cepLightboxReady = 'true';
    const canvas = dialog.querySelector('[data-cep-canvas]');
    const count = dialog.querySelector('[data-cep-count]');
    const photo = new Image();
    photo.className = 'cep-lightbox__image';
    photo.decoding = 'async';
    photo.draggable = false;
    canvas.append(photo);
    let index = 0;
    let loading = false;
    const loadingIndicator = document.createElement('span');
    loadingIndicator.className = 'cep-lightbox__loading';
    loadingIndicator.setAttribute('role', 'status');
    loadingIndicator.setAttribute('aria-label', dialog.dataset.loadingLabel || 'Loading photo');
    loadingIndicator.hidden = true;
    dialog.append(loadingIndicator);
    const setLoading = (value) => {
      loading = value;
      loadingIndicator.hidden = !value;
      canvas.setAttribute('aria-busy', String(value));
      dialog.querySelectorAll('[data-cep-prev], [data-cep-next]').forEach(button => { button.disabled = value; });
    };
    photo.addEventListener('load', () => setLoading(false));
    photo.addEventListener('error', () => setLoading(false));
    let gesture = null;
    let swipedAt = 0;
    let restoreKeyboardFocus = false;
    const display = (next) => {
      if (loading) return;
      canvas.classList.remove('is-zoomed');
      index = (next + photos.length) % photos.length;
      const source = photos[index];
      photo.alt = source.alt;
      setLoading(true);
      photo.src = source.src;
      if (photo.complete) setLoading(false);
      count.textContent = `${index + 1} / ${photos.length}`;
      if (radios[index] && !radios[index].checked) radios[index].click();
    };
    photo.tabIndex = 0;
    const toggleZoom = () => {
      if (loading || Date.now() - swipedAt < 500) return;
      if (canvas.classList.contains('is-zoomed')) {
        canvas.classList.remove('is-zoomed');
        return;
      }
      const width = photo.getBoundingClientRect().width;
      photo.style.setProperty('--cep-zoom-width', `${Math.max(width * 2, Math.min(photo.naturalWidth, width * 3))}px`);
      canvas.classList.add('is-zoomed');
      canvas.scrollLeft = Math.max(0, (canvas.scrollWidth - canvas.clientWidth) / 2);
      canvas.scrollTop = Math.max(0, (canvas.scrollHeight - canvas.clientHeight) / 2);
    };
    photo.addEventListener('click', toggleZoom);
    photo.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggleZoom(); }
    });
    trigger.hidden = false;
    trigger.addEventListener('click', (event) => {
      if (event.detail > 0 && Date.now() - Number(gallery.dataset.cepSwipeAt || 0) < 500) return;
      restoreKeyboardFocus = event.detail === 0;
      display(Math.max(0, radios.findIndex((radio) => radio.checked)));
      dialog.showModal();
      dialog.focus({ preventScroll: true });
    });
    dialog.querySelector('[data-cep-close]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-cep-prev]').addEventListener('click', () => display(index - 1));
    dialog.querySelector('[data-cep-next]').addEventListener('click', () => display(index + 1));
    if (photos.length < 2) {
      dialog.querySelector('[data-cep-prev]').hidden = true;
      dialog.querySelector('[data-cep-next]').hidden = true;
    }
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        display(index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    dialog.addEventListener('click', (event) => {
      if (Date.now() - swipedAt < 500) return;
      if (event.target === dialog || event.target === canvas) dialog.close();
    });
    dialog.addEventListener('close', () => {
      setLoading(false);
      gesture = null;
      if (restoreKeyboardFocus) trigger.focus({ preventScroll: true });
      else trigger.blur();
      restoreKeyboardFocus = false;
    });
    canvas.addEventListener('touchstart', (event) => {
      gesture = !canvas.classList.contains('is-zoomed') && event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    canvas.addEventListener('touchend', (event) => {
      if (!gesture || event.changedTouches.length !== 1 || event.touches.length) { gesture = null; return; }
      const x = event.changedTouches[0].clientX - gesture.x;
      const y = event.changedTouches[0].clientY - gesture.y;
      gesture = null;
      if (Math.abs(x) < 44 || Math.abs(x) <= Math.abs(y) * 1.2) return;
      swipedAt = Date.now();
      display(index + (x < 0 ? 1 : -1));
    }, { passive: true });
    canvas.addEventListener('touchcancel', () => { gesture = null; }, { passive: true });
  };

  const initializeGallery = (gallery) => {
    initializeLightbox(gallery);
    if (gallery.dataset.cepSwipeReady === 'true') return;

    const stage = gallery.querySelector('.cep-stage');
    const radios = Array.from(gallery.querySelectorAll('.cep-radio'));
    if (!stage || radios.length < 2) return;

    gallery.dataset.cepSwipeReady = 'true';

    let gestureStart = null;

    const resetGesture = () => {
      gestureStart = null;
    };

    const selectRelativeSlide = (offset) => {
      const currentIndex = Math.max(0, radios.findIndex((radio) => radio.checked));
      const nextIndex = (currentIndex + offset + radios.length) % radios.length;
      radios[nextIndex].click();
    };

    stage.addEventListener(
      'touchstart',
      (event) => {
        if (!mobileViewport.matches || event.touches.length !== 1) {
          resetGesture();
          return;
        }

        const touch = event.touches[0];
        gestureStart = {
          x: touch.clientX,
          y: touch.clientY,
        };
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        if (!gestureStart || !mobileViewport.matches || event.changedTouches.length !== 1) {
          resetGesture();
          return;
        }

        const touch = event.changedTouches[0];
        const distanceX = touch.clientX - gestureStart.x;
        const distanceY = touch.clientY - gestureStart.y;
        const horizontalDistance = Math.abs(distanceX);
        const verticalDistance = Math.abs(distanceY);
        const swipeThreshold = Math.max(44, Math.min(stage.clientWidth * 0.12, 72));

        resetGesture();

        if (horizontalDistance < swipeThreshold || horizontalDistance <= verticalDistance * 1.2) return;

        gallery.dataset.cepSwipeAt = String(Date.now());
        selectRelativeSlide(distanceX < 0 ? 1 : -1);
      },
      { passive: true }
    );

    stage.addEventListener('touchcancel', resetGesture, { passive: true });
  };

  const initializeWithin = (root) => {
    if (root instanceof Element && root.matches(gallerySelector)) initializeGallery(root);
    root.querySelectorAll?.(gallerySelector).forEach(initializeGallery);
  };

  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
